import React, { useContext, useEffect, useRef } from 'react';
import { arrayOf, func, number, oneOfType, shape, string } from 'prop-types';
import { Box, Typography } from '@mui/material';
import * as R from 'ramda';
import { useLocation } from 'react-router-dom';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

// Components
import { MAutocomplete, MDropdown } from 'common';

// Contexts
import { AuthContext } from 'authentication/AuthContext';

// Utils
import { useGetAffiliateOptions } from 'hooks';
import { STEP_ONE_SUBHEADER } from 'feature/loading-management/utils/const';
import { capitalize } from 'utils/helpers/format';
import { findOptionByValue } from 'utils/helpers/function';
import { EvaluateFlag } from 'common/FeatureFlags/Flag';

const DropDownSection = ({
  authorizationBy,
  currentSelection,
  inventoryOwner,
  fetchLocations,
  fetchProducts,
  fetchSuppliers,
  supplierIdName,
  setWizardDetails,
  setParams,
  setHasUnsavedChanges,
  setViewByListFetched,
  setViewByList,
  supplierListOptions,
  viewById,
  viewByListOptions,
}) => {
  const { pathname } = useLocation();
  const { currentAffiliate } = useContext(AuthContext);
  const affiliatesOptions = useGetAffiliateOptions(['supplier']);
  const isCreateSupplierAuthorizationsEnabled = EvaluateFlag(
    process.env.REACT_APP_LD_CREATE_SUPPLIER_AUTHORIZATIONS
  );

  // either set affiliate to the currentAffiliate in selfAuthorization store or default to the first one in userAffiliate options
  const selectedAffiliate = R.either(
    R.find(R.propEq('value', R.defaultTo(currentAffiliate?.value, inventoryOwner.value))),
    R.head
  )(affiliatesOptions);

  // Note: as component inside the drawer component, that is rendered at the base route level
  // '/' means that the component here would not have access to the URL params via hook
  // useParams. It has access to other react router hooks like useLocation and useNavigate
  // that we need to disect in order to have authorizationType and viewBy.
  // TODO: in order to consider useage of all hooks from react router
  // could be part of CASREF-2421 moving it out of the App level base route of '/'
  // and allow the Drawer to be passed in to any component it may be needed so that
  // it could utilize all of react routers hooks for code simplicity
  const routePath = pathname.split('/');
  const authType = routePath[5];
  const onSupplierAuth = authType === 'supplier-authorizations';
  const viewBy = routePath[6];
  const selectedSupplierOption = findOptionByValue(supplierIdName, supplierListOptions);
  const selectedViewByListOption = findOptionByValue(viewById, viewByListOptions);

  // Step 2 Control:
  const shouldFetchSuppliers = useRef(false);
  // Do not fetch suppliers when changing steps and until there is an inventory owner
  const fetchSuppliersControl = shouldFetchSuppliers.current && inventoryOwner.dataCompanyId;

  // Step 2a Control:
  const shouldUpdateSupplier = useRef(true);
  // Do not automatically update supplier when
  const updateSupplierControl =
    // changing inventory owner
    !shouldUpdateSupplier.current ||
    // coming back a step
    supplierIdName ||
    // there are no options
    !supplierListOptions.length ||
    // outside of supplier auth
    !onSupplierAuth;

  // Step 3 control: Do not fetch on initial render or when switching steps
  const shouldFetchViewByList = useRef(false);

  // Step 4 control:
  const shouldUpdateViewById = useRef(true);
  // Do not update when changing steps, there are no viewBy options, on initial render, or on supplier auth viewing by supplier
  const updateViewByIdControl =
    viewById ||
    !viewByListOptions.length ||
    !shouldUpdateViewById.current ||
    (onSupplierAuth && viewBy === 'supplier');

  // Step 1: Choose proper authorization and current affiliate
  useEffect(() => {
    // authorizationBy defaults to "location", only change if it is "product". Avoid setting to "supplier"
    if (viewBy === 'product') {
      setWizardDetails({
        name: 'authorizationBy',
        value: viewBy,
      });
    }
    // choose inventoryOwner on inital render
    if (!inventoryOwner.dataCompanyId) {
      // inventoryOwner will only be undefined when initially opening drawer
      // this allows us to keep from refetching suppliers when going back a step
      shouldFetchSuppliers.current = true;
      setWizardDetails({
        name: 'inventoryOwner',
        value: {
          companyCustomerIdName: selectedAffiliate.customerIdName,
          dataCompanyId: selectedAffiliate.dataCompanyId,
          name: selectedAffiliate.label,
          value: selectedAffiliate.value,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Step 2: Fetch suppliers
  useEffect(() => {
    if (fetchSuppliersControl) {
      fetchSuppliers({
        inventoryOwner: {
          companyCustomerIdName: inventoryOwner.companyCustomerIdName,
          dataCompanyId: inventoryOwner.dataCompanyId,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryOwner.dataCompanyId, inventoryOwner.companyCustomerIdName]);

  // Step 2a: Choose first supplier in list or chosen supplier if viewing by supplier
  useEffect(() => {
    // ensure the supplier is only defaulted on initial render when in supplier authorizations module
    if (updateSupplierControl) return;
    if (viewBy === 'supplier') {
      setParams({ supplierIdName: currentSelection.idName });
    } else {
      setParams({ supplierIdName: supplierListOptions[0].value });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierListOptions]);

  // Step 3: Fetch locations or products
  useEffect(() => {
    if (!shouldFetchViewByList.current) {
      shouldFetchViewByList.current = true;
      return;
    }

    // reset viewById on any supplier change
    setWizardDetails({ name: 'viewById', value: null });
    setViewByListFetched(false);
    setViewByList([]);

    if (supplierIdName && !R.isEmpty(selectedSupplierOption)) {
      if (authorizationBy === 'product') {
        fetchProducts({
          inventoryOwner: {
            companyCustomerIdName: inventoryOwner.companyCustomerIdName,
            dataCompanyId: inventoryOwner.dataCompanyId,
          },
          supplier: {
            companyCustomerIdName: selectedSupplierOption?.value,
            dataCompanyId: selectedSupplierOption?.dataCompanyId,
          },
        });
      } else {
        fetchLocations({
          inventoryOwner: {
            companyCustomerIdName: inventoryOwner.companyCustomerIdName,
            dataCompanyId: inventoryOwner.dataCompanyId,
          },
          supplier: {
            companyCustomerIdName: selectedSupplierOption?.value,
            dataCompanyId: selectedSupplierOption?.dataCompanyId,
          },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierIdName, authorizationBy]);

  // Step 3a: Choose first id in list or chosen id if in supplier Auth
  useEffect(() => {
    if (updateViewByIdControl) return;
    shouldUpdateViewById.current = false;
    if (viewBy) {
      // Ensures that if product/location is not in list, it will default to first value
      const id = `${authorizationBy}Id`;
      const availableSelection = R.any(R.propEq('value', currentSelection[id]));
      availableSelection(viewByListOptions)
        ? setWizardDetails({ name: 'viewById', value: currentSelection[id] })
        : setWizardDetails({ name: 'viewById', value: viewByListOptions[0].value });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewByListOptions]);

  const handleSupplierChange = val => {
    setHasUnsavedChanges(true);
    shouldUpdateViewById.current = false;
    setParams({ supplierIdName: R.prop('value', val) });
  };

  const handleAffiliateChange = ({ name, value }) => {
    // control ref to stop fetch of suppliers if coming back a step
    shouldFetchSuppliers.current = true;
    shouldUpdateSupplier.current = false;
    setHasUnsavedChanges(true);
    setParams({ supplierIdName: null });
    const selected = findOptionByValue(value, affiliatesOptions);
    setWizardDetails({
      name,
      value: {
        companyCustomerIdName: selected.customerIdName,
        dataCompanyId: selected.dataCompanyId,
        name: selected.label,
        value: selected.value,
      },
    });
  };

  return (
    <Box display="flex" flexDirection="column">
      <Typography color="textPrimary" fontWeight={700} mb={1} pt={2} variant="body1">
        {isCreateSupplierAuthorizationsEnabled ? 'Authorization Setup' : STEP_ONE_SUBHEADER}
      </Typography>
      <Typography color="textSecondary" fontFamily="Overpass" fontWeight={400} variant="body2">
        {isCreateSupplierAuthorizationsEnabled && 'Setup your desired authorization'}
      </Typography>
      <Box sx={{ display: 'grid', mt: 3, rowGap: 2 }}>
        {affiliatesOptions.length > 1 && (
          <MDropdown
            disableAutoLabelColor
            id="affiliate"
            items={affiliatesOptions}
            label="Affiliate"
            name="inventoryOwner"
            onChange={handleAffiliateChange}
            styleProps={{
              menu: { transformOrigin: { horizontal: 'left', vertical: 'top' } },
            }}
            value={selectedAffiliate.value}
          />
        )}
        <MAutocomplete
          id="supplier"
          label="Supplier"
          loading={false}
          name="supplierIdName"
          onChange={R.flip(handleSupplierChange)}
          openOnFocus
          options={supplierListOptions}
          popupIcon={<ArrowDropDownIcon />}
          value={selectedSupplierOption?.value}
        />
        {!isCreateSupplierAuthorizationsEnabled && (
          <MAutocomplete
            id={authorizationBy}
            label={capitalize(authorizationBy)}
            loading={false}
            name="viewById"
            onChange={(_, val) =>
              setWizardDetails({ name: 'viewById', value: R.prop('value', val) })
            }
            openOnFocus
            options={viewByListOptions}
            value={selectedViewByListOption?.value}
          />
        )}
      </Box>
    </Box>
  );
};

DropDownSection.propTypes = {
  authorizationBy: string.isRequired,
  currentSelection: shape({}),
  fetchLocations: func.isRequired,
  fetchProducts: func.isRequired,
  fetchSuppliers: func.isRequired,
  inventoryOwner: shape({
    dataCompanyId: number,
    label: string,
  }).isRequired,
  setHasUnsavedChanges: func.isRequired,
  setParams: func.isRequired,
  setViewByList: func.isRequired,
  setViewByListFetched: func.isRequired,
  setWizardDetails: func.isRequired,
  supplierIdName: oneOfType([string, number]),
  supplierListOptions: arrayOf(
    shape({
      label: string,
      value: string,
    })
  ),
  viewById: number,
  viewByListOptions: arrayOf(
    shape({
      label: string,
      value: number,
    })
  ),
};

DropDownSection.defaultProps = {
  currentSelection: {},
  supplierIdName: '',
  supplierListOptions: [],
  viewById: null,
  viewByListOptions: [],
};

export default DropDownSection;
